/**
 * localStorage persistence for Best Odds / Market Anomalies filters.
 * Intentionally separate from arbitrage FilterPersistence
 * (key: arbiwin_filter_preferences).
 */

import {
  DEFAULT_BEST_ODDS_FILTERS,
  TIME_WINDOW_VALUES
} from './bestOddsFilters';

export const BEST_ODDS_FILTERS_STORAGE_KEY = 'bestOddsFilters';

const STORAGE_VERSION = 1;

/**
 * @param {unknown} value
 * @returns {string[]|null}
 */
const sanitizeStringArrayOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (!Array.isArray(value)) {
    return null;
  }

  const cleaned = value
    .filter((item) => item !== null && item !== undefined && String(item).trim().length > 0)
    .map((item) => String(item));

  return cleaned;
};

/**
 * Normalize a raw/partial filters object into a safe shape.
 * @param {Object|null|undefined} raw
 * @returns {Object}
 */
export const normalizeBestOddsFilters = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {};

  const minSpreadRaw = Number(source.minSpread);
  const minSpread = Number.isFinite(minSpreadRaw) && minSpreadRaw > 0
    ? minSpreadRaw
    : 0;

  const maxOddsRaw = Number(source.maxOdds);
  const maxOdds = Number.isFinite(maxOddsRaw) && maxOddsRaw > 0
    ? maxOddsRaw
    : 0;

  const searchQuery = typeof source.searchQuery === 'string'
    ? source.searchQuery
    : '';

  const timeWindow = TIME_WINDOW_VALUES.includes(source.timeWindow)
    ? source.timeWindow
    : DEFAULT_BEST_ODDS_FILTERS.timeWindow;

  return {
    minSpread,
    maxOdds,
    searchQuery,
    selectedBookmakers: sanitizeStringArrayOrNull(source.selectedBookmakers),
    selectedChampionships: sanitizeStringArrayOrNull(source.selectedChampionships),
    selectedMarketKeys: sanitizeStringArrayOrNull(source.selectedMarketKeys),
    timeWindow
  };
};

/**
 * Load filters from localStorage. Returns defaults when missing/invalid.
 * @returns {Object}
 */
export const loadBestOddsFilters = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return { ...DEFAULT_BEST_ODDS_FILTERS };
    }

    const raw = localStorage.getItem(BEST_ODDS_FILTERS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_BEST_ODDS_FILTERS };
    }

    const parsed = JSON.parse(raw);
    return normalizeBestOddsFilters(parsed);
  } catch (error) {
    console.warn('BestOdds filter persistence: failed to load, using defaults', error);
    return { ...DEFAULT_BEST_ODDS_FILTERS };
  }
};

/**
 * Save filters to localStorage.
 * @param {Object} filters
 * @returns {boolean} true when save succeeded
 */
export const saveBestOddsFilters = (filters) => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    const normalized = normalizeBestOddsFilters(filters);
    const payload = {
      version: STORAGE_VERSION,
      ...normalized,
      lastUpdated: new Date().toISOString()
    };

    localStorage.setItem(BEST_ODDS_FILTERS_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch (error) {
    console.warn('BestOdds filter persistence: failed to save', error);
    return false;
  }
};

/**
 * Remove persisted Best Odds filters.
 * @returns {boolean}
 */
export const clearBestOddsFiltersStorage = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    localStorage.removeItem(BEST_ODDS_FILTERS_STORAGE_KEY);
    return true;
  } catch (error) {
    console.warn('BestOdds filter persistence: failed to clear', error);
    return false;
  }
};

export default {
  BEST_ODDS_FILTERS_STORAGE_KEY,
  normalizeBestOddsFilters,
  loadBestOddsFilters,
  saveBestOddsFilters,
  clearBestOddsFiltersStorage
};
